import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, 
  FileText, 
  DollarSign, 
  AlertTriangle, 
  Clock,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../services/api';
import { formatCurrency, formatCurrencyAbbreviated, formatDate } from '../utils/formatters';

const Dashboard = () => {
  const { data: dashboardData, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardService.resumo,
    staleTime: 300000, // 5 minutos (aumentado para reduzir requisições)
    gcTime: 600000, // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Não refetch ao montar se já tem dados
  });

  // Usar dados do resumo principal para evitar queries extras
  const data = dashboardData || {};
  const clientesPrioritarios = data.clientes_prioritarios || [];
  const contasVencidas = data.contas_vencidas_list || [];
  const contasVencendoBreve = data.contas_vencendo_breve_list || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                <div className="ml-3 flex-1">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <button
            onClick={() => refetch()}
            className="btn-primary flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
        <div className="card">
          <div className="text-center text-red-600 dark:text-red-400">
            Erro ao carregar dados do dashboard
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <button
          onClick={() => refetch()}
          className="btn-secondary flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 xl:grid-cols-5">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Total de Clientes</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{data.total_clientes || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <FileText className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Contas em Aberto</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{data.total_contas_abertas || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Valor Total</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white break-words" title={formatCurrency(data.total_valor_aberto || 0)}>
                {formatCurrencyAbbreviated(data.total_valor_aberto || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Contas Vencidas</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{data.contas_vencidas || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <DollarSign className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Valor em Atraso</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white break-words" title={formatCurrency(data.total_valor_em_atraso || 0)}>
                {formatCurrencyAbbreviated(data.total_valor_em_atraso || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos e Listas */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Clientes Prioritários */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Clientes Prioritários</h2>
            <Link to="/clientes" className="text-primary-600 dark:text-primary-400 hover:underline text-sm">
              Ver Todos
            </Link>
          </div>
          <div className="space-y-3">
            {clientesPrioritarios.slice(0, 5).map((cliente) => (
              <div key={cliente.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={cliente.nome_cliente}>
                    {cliente.nome_cliente}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {cliente.contas_vencidas || 0} contas vencidas
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(cliente.total_valor_aberto || 0)}
                  </p>
                  <Link 
                    to={`/clientes/${cliente.id}`}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Ver Detalhes
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contas Vencidas */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contas Vencidas</h2>
            <Link to="/contas-receber" className="text-primary-600 dark:text-primary-400 hover:underline text-sm">
              Ver Todas
            </Link>
          </div>
          <div className="space-y-3">
            {contasVencidas.slice(0, 5).map((conta) => (
              <div key={conta.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={conta.cliente_nome}>
                    {conta.cliente_nome}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Venceu em {formatDate(conta.vencimento_rec)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(conta.valor_restante || 0)}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {conta.dias_vencimento || 0} dias atrasado
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contas Vencendo em Breve */}
      {contasVencendoBreve.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Vencendo em Breve</h2>
            <Clock className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {contasVencendoBreve.slice(0, 6).map((conta) => (
              <div key={conta.id} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={conta.cliente_nome}>
                  {conta.cliente_nome}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Vence em {formatDate(conta.vencimento_rec)}
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                  {formatCurrency(conta.valor_restante || 0)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 